import { resetLoadoutForCheckpointRestart } from "../systems/memory.js";
import { hideLightingOverlay } from "../systems/lighting.js";
import { closeUI } from "../ui/memoryUI.js";
import { closeEscapeUI } from "../ui/escapeUI.js";
import { closeTextbox } from "../ui/textboxUI.js";
import { resetGameHud } from "../ui/gameHud.js";
import { ensureCrosshairOverlay, showCrosshairAt } from "../ui/crosshair.js";

/** Mirror boot layout so crash → reboot reads as one terminal sequence. */
const CRASH_GRAPH_Z = 5;
const CRASH_DIAG_Z = 4;
const CRASH_LINE = [255, 70, 90];
const CRASH_BANNER = [255, 70, 90];

function getCrashLayout() {
    const graphW = Math.min(220, Math.max(150, width() - 120));
    const graphH = 52;
    const labelW = 54;
    const totalW = labelW + 10 + graphW;
    const graphY = Math.round(center().y + 28);
    const labelX = Math.round(center().x - totalW / 2);
    const bannerY = Math.round(center().y - 110);

    return {
        graphW,
        graphH,
        labelX,
        labelY: graphY + 17,
        graphX: labelX + labelW + 10,
        graphY,
        bannerY,
        promptY: graphY + graphH + 48,
        diagX: Math.round(center().x - 160),
        diagY: bannerY + 36,
    };
}

function renderCrashCpuFlatline() {
    destroyAll("crash_cpu_graph");
    const layout = getCrashLayout();
    const chartX = layout.graphX + 8;
    const chartY = layout.graphY + 8;
    const chartW = layout.graphW - 16;
    const chartH = layout.graphH - 16;
    const midY = chartY + Math.round(chartH * 0.5);

    add([
        text("CPU", { font: "ShareTechMono", size: 14 }),
        pos(layout.labelX, layout.labelY),
        color(180, 100, 110),
        fixed(),
        z(CRASH_GRAPH_Z),
        "crash_cpu_graph",
    ]);

    for (let gx = 1; gx < 5; gx++) {
        add([
            rect(1, chartH),
            pos(chartX + gx * Math.floor(chartW / 5), chartY),
            color(34, 18, 22),
            fixed(),
            z(CRASH_GRAPH_Z + 1),
            "crash_cpu_graph",
        ]);
    }

    add([
        rect(chartW + 1, 2),
        pos(chartX, midY),
        color(...CRASH_LINE),
        fixed(),
        z(CRASH_GRAPH_Z + 2),
        "crash_cpu_graph",
    ]);
}

function renderCrashDiagnostics(reason) {
    destroyAll("crash_diag");
    const layout = getCrashLayout();
    const detail = reason === "damage"
        ? "Chassis integrity failed."
        : "Kernel removed from RAM.";
    const lines = [
        "FATAL ..................... HALT",
        detail.length > 34 ? detail.slice(0, 34) : detail,
        "SAFE_MODE ................. QUEUED",
        "REBOOT_SEQ ................ STANDBY",
    ];

    lines.forEach((line, i) => {
        add([
            text(line, { font: "ShareTechMono", size: 11 }),
            pos(layout.diagX, layout.diagY + i * 14),
            color(160, 60, 70),
            opacity(0.9),
            fixed(),
            z(CRASH_DIAG_Z),
            "crash_diag",
        ]);
    });
}

export function registerCrashScene() {
    scene("crash", (args = {}) => {
        hideLightingOverlay();
        resetGameHud();
        closeUI();
        closeEscapeUI();
        closeTextbox();

        const canvas = document.querySelector("canvas");
        if (canvas) canvas.style.cursor = "none";
        ensureCrosshairOverlay();

        const layout0 = getCrashLayout();

        const bg = add([
            rect(width(), height()),
            pos(0, 0),
            color(0, 0, 0),
            fixed(),
        ]);

        const banner = add([
            text("SYSTEM CRASH", { font: "ShareTechMono", size: 22 }),
            pos(center().x, layout0.bannerY),
            anchor("center"),
            color(...CRASH_BANNER),
            fixed(),
        ]);

        const prompt = add([
            text("Press SPACE or Click to Reboot", {
                font: "ShareTechMono",
                size: 16,
            }),
            pos(center().x, layout0.promptY),
            anchor("center"),
            color(255, 200, 100),
            fixed(),
        ]);

        renderCrashDiagnostics(args.reason);
        renderCrashCpuFlatline();

        onResize(() => {
            const layout = getCrashLayout();
            bg.width = width();
            bg.height = height();
            banner.pos = vec2(center().x, layout.bannerY);
            prompt.pos = vec2(center().x, layout.promptY);
            renderCrashDiagnostics(args.reason);
            renderCrashCpuFlatline();
        });

        onUpdate(() => {
            showCrosshairAt(mousePos());
        });

        let leaving = false;

        function goToBoot() {
            if (leaving) return;
            leaving = true;
            prompt.text = "";
            // Position and RAM/loadout restore to the previous checkpoint.
            resetLoadoutForCheckpointRestart();
            go("boot", {
                autoStart: true,
                resetMemory: false,
                kernelOnlyMemory: false,
                openMemoryOnInit: false,
                respawnFromCheckpoint: true,
            });
        }

        onKeyPress("space", goToBoot);
        onKeyPress("r", goToBoot);
        // α.26+: bare onClick(fn) removed — use onMousePress
        onMousePress(goToBoot);
    });
}
